import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment, { Moment } from "moment";
import "moment/locale/pt";
import Calendar, { Mode } from "rc-calendar";
import ptPT from "rc-calendar/lib/locale/pt_PT";
import { MGResponsiveSheet } from "../MGResponsiveSheet";
import calendarIcon from "../../../images/calendar.svg";
import styles from "./MGDatePicker.module.css";
import { MGButton } from "../MGButton";
import { MGField, TMGFieldProps, useFieldId } from "../MGField/MGField";
import fieldStyles from "../MGField/MGField.module.css";

const DATE_FORMAT = "DDMMYYYY";
const CALENDAR_PREFIX = "MGDatePickerCalendar";
const MOMENT_LOCALE = "pt-mg-date-picker";
const calendarLocale = { ...ptPT, monthFormat: "MMMM" };

if (moment.locales().indexOf(MOMENT_LOCALE) === -1) {
  moment.defineLocale(MOMENT_LOCALE, {
    parentLocale: "pt",
    weekdaysMin: ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"],
  });
  moment.locale("pt");
}

export type TMGDatePickerProps = Omit<TMGFieldProps, "labelText"> & {
  value?: Date | null;
  defaultValue?: Date | null;
  labelText: string;
  pickerTitle?: string;
  pickerDescription?: string;
  confirmLabel?: string;
  minDate?: Date;
  maxDate?: Date;
  disabledDate?: (date: Date) => boolean;
  name?: string;
  onChange?: (date: Date | null) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onOpenChange?: (open: boolean) => void;
};

const toMoment = (date?: Date | null): Moment | null => {
  if (!date || isNaN(date.getTime())) return null;
  return moment(date).locale(MOMENT_LOCALE).startOf("day");
};

const formatDate = (date?: Date | null) => {
  const dateMoment = toMoment(date);
  return dateMoment ? dateMoment.format("DD / MM / YYYY") : "";
};

const maskDate = (rawValue: string) => {
  const digits = rawValue.replace(/\D/g, "").slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);

  if (digits.length <= 2) return day;
  if (digits.length <= 4) return `${day} / ${month}`;
  return `${day} / ${month} / ${year}`;
};

const parseDate = (inputValue: string): Moment | null => {
  const digits = inputValue.replace(/\D/g, "");
  if (digits.length !== 8) return null;

  const parsedDate = moment(digits, DATE_FORMAT, true).locale(MOMENT_LOCALE).startOf("day");
  return parsedDate.isValid() ? parsedDate : null;
};

export const MGDatePicker = ({
  id: externalId,
  value,
  defaultValue = null,
  labelText,
  helperText,
  error = false,
  errorText,
  pickerTitle = "Escolha a data",
  pickerDescription,
  confirmLabel = "Confirmar",
  minDate,
  maxDate,
  disabledDate,
  disabled = false,
  readOnly = false,
  required = false,
  background,
  showLabels,
  name,
  dataTestId,
  className,
  style,
  onChange,
  onBlur,
  onOpenChange,
}: TMGDatePickerProps) => {
  const inputId = useFieldId(externalId);
  const [internalValue, setInternalValue] = useState<Date | null>(defaultValue);
  const [inputValue, setInputValue] = useState(
    formatDate(value === undefined ? defaultValue : value),
  );
  const [validationError, setValidationError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [draftDate, setDraftDate] = useState<Moment | null>(null);
  const [calendarView, setCalendarView] = useState<Moment>(() => moment().locale(MOMENT_LOCALE));
  const [calendarMode, setCalendarMode] = useState<Mode>("date");
  const calendarRef = useRef<HTMLDivElement>(null);

  const selectedDate = value === undefined ? internalValue : value;
  const minDateMoment = toMoment(minDate);
  const maxDateMoment = toMoment(maxDate);
  const displayedError = errorText || validationError;
  const hasError = !!(error || validationError);
  const displayedMessage = (hasError && displayedError) || helperText;
  const messageId = displayedMessage ? `${inputId}-message` : undefined;

  useEffect(() => {
    setInputValue(formatDate(selectedDate));
    setValidationError("");
  }, [selectedDate]);

  useEffect(() => {
    const calendar = calendarRef.current;
    if (!isOpen || !calendar) return;

    const prepareControls = () => {
      calendar.querySelectorAll<HTMLElement>(`.${CALENDAR_PREFIX}-header a`).forEach((control) => {
        control.tabIndex = 0;
        control.setAttribute("role", "button");
      });

      const activePanel =
        calendarMode === "date"
          ? calendar
          : calendar.querySelector(`.${CALENDAR_PREFIX}-${calendarMode}-panel`);

      if (activePanel && !activePanel.contains(document.activeElement)) {
        const selector =
          calendarMode === "date"
            ? `.${CALENDAR_PREFIX}-month-select`
            : `.${CALENDAR_PREFIX}-${calendarMode}-panel-header a`;

        const control = calendar.querySelector<HTMLElement>(selector);

        if (control) control.focus({ preventScroll: true });
      }
    };

    prepareControls();

    const observer = new MutationObserver(prepareControls);
    observer.observe(calendar, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isOpen, calendarMode, calendarView]);

  const isDateDisabled = (date: Moment) => {
    if (minDateMoment && date.isBefore(minDateMoment, "day")) return true;
    if (maxDateMoment && date.isAfter(maxDateMoment, "day")) return true;
    return disabledDate ? disabledDate(date.toDate()) : false;
  };

  const calendarWeekCount = useMemo(() => {
    const firstDayOfMonth = calendarView.clone().startOf("month");
    const firstDayOffset =
      (firstDayOfMonth.day() + 7 - firstDayOfMonth.localeData().firstDayOfWeek()) % 7;
    return Math.ceil((firstDayOffset + calendarView.daysInMonth()) / 7);
  }, [calendarView]);

  const updateValue = (nextValue: Date | null) => {
    if (value === undefined) setInternalValue(nextValue);
    setInputValue(formatDate(nextValue));
    setValidationError("");
    if (onChange) onChange(nextValue);
  };

  const validateAndUpdateInput = (nextInputValue: string) => {
    if (!nextInputValue) {
      updateValue(null);
      return true;
    }

    const parsedDate = parseDate(nextInputValue);
    if (!parsedDate) {
      setValidationError("Introduza uma data válida.");
      return false;
    }

    if (isDateDisabled(parsedDate)) {
      setValidationError("A data introduzida não está disponível.");
      return false;
    }

    updateValue(parsedDate.toDate());
    return true;
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextInputValue = maskDate(event.target.value);
    setInputValue(nextInputValue);
    setValidationError("");

    if (nextInputValue.replace(/\D/g, "").length === 8) {
      validateAndUpdateInput(nextInputValue);
    }
  };

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    validateAndUpdateInput(inputValue);
    if (onBlur) onBlur(event);
  };

  const openPicker = () => {
    if (disabled || readOnly) return;

    const currentDate = toMoment(selectedDate);
    let nextCalendarView = currentDate || moment().locale(MOMENT_LOCALE).startOf("day");

    if (minDateMoment && nextCalendarView.isBefore(minDateMoment, "day")) {
      nextCalendarView = minDateMoment.clone();
    }

    if (maxDateMoment && nextCalendarView.isAfter(maxDateMoment, "day")) {
      nextCalendarView = maxDateMoment.clone();
    }

    setDraftDate(currentDate);
    setCalendarView(nextCalendarView);
    setCalendarMode("date");
    setIsOpen(true);

    if (onOpenChange) onOpenChange(true);
  };

  const closePicker = () => {
    setIsOpen(false);
    if (onOpenChange) onOpenChange(false);
  };

  const confirmDate = () => {
    if (calendarMode !== "date" || !draftDate || isDateDisabled(draftDate)) return;
    updateValue(draftDate.toDate());
    closePicker();
  };

  const handleCalendarSelect = (nextDate: Moment) => {
    setDraftDate(nextDate.clone());
  };

  const handleCalendarViewChange = (nextDate: Moment | null) => {
    if (nextDate) setCalendarView(nextDate.clone().locale(MOMENT_LOCALE));
  };

  const handleCalendarKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const control = (event.target as HTMLElement).closest("a");
    if (!control) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      control.click();
    } else if (calendarMode !== "date") {
      event.stopPropagation();
    }
  };

  const calendarSizeClass = useMemo(() => {
    if (calendarWeekCount === 4) return styles.fourWeekCalendar;
    if (calendarWeekCount === 5) return styles.fiveWeekCalendar;

    return "";
  }, [calendarWeekCount]);

  return (
    <MGField
      id={inputId}
      labelText={labelText}
      helperText={helperText}
      error={hasError}
      errorText={displayedError}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      showLabels={showLabels}
      background={background}
      className={`${styles.datePicker} ${className || ""}`}
      style={style}
      dataTestId={dataTestId}
      filled={!!inputValue}>
      <div className={fieldStyles.control}>
        <input
          className={fieldStyles.input}
          id={inputId}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="DD / MM / AAAA"
          value={inputValue}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-invalid={hasError}
          aria-describedby={messageId}
          data-testid={dataTestId && `${dataTestId}-input`}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
        />
        {!(disabled || readOnly) && (
          <button
            className={fieldStyles.iconButton}
            type="button"
            aria-label="Abrir calendário"
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            disabled={disabled || readOnly}
            data-testid={dataTestId && `${dataTestId}-calendar-button`}
            onClick={openPicker}>
            <img className={styles.calendarIcon} src={calendarIcon} alt="" />
          </button>
        )}
      </div>

      <MGResponsiveSheet
        open={isOpen}
        title={pickerTitle}
        desktopMaxWidth={480}
        dataTestId={dataTestId && `${dataTestId}-sheet`}
        sheetClassName={styles.sheet}
        titleClassName={styles.sheetTitle}
        bodyClassName={styles.sheetBody}
        footerClassName={styles.sheetFooter}
        onClose={closePicker}
        footer={
          <MGButton
            fullWidth
            className={styles.confirmAction}
            dataTestId={dataTestId && `${dataTestId}-confirm`}
            disabled={!(calendarMode === "date" && !!draftDate && !isDateDisabled(draftDate))}
            onClick={confirmDate}>
            {confirmLabel}
          </MGButton>
        }>
        {pickerDescription && <p className={styles.pickerDescription}>{pickerDescription}</p>}

        <div
          ref={calendarRef}
          className={`${styles.calendar} ${calendarSizeClass} ${calendarMode !== "date" ? styles.periodSelection : ""}`}
          onKeyDownCapture={handleCalendarKeyDown}>
          <Calendar
            prefixCls={CALENDAR_PREFIX}
            locale={calendarLocale}
            value={calendarView}
            mode={calendarMode}
            selectedValue={draftDate || undefined}
            showDateInput={false}
            showToday={false}
            showOk={false}
            disabledDate={(date) => !!date && isDateDisabled(date)}
            onChange={handleCalendarViewChange}
            onPanelChange={(_, mode) => setCalendarMode(mode)}
            onSelect={handleCalendarSelect}
          />
        </div>
      </MGResponsiveSheet>
    </MGField>
  );
};
